import { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Select, DatePicker, InputNumber, Button, Row, Col, Tag, Input, EmptyState, EntityAvatar, message, cn } from '../../ui';
import { Trash2, Plus, ArrowRight, Warehouse, PackagePlus, PackageSearch, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { warehouseOption, warehouseText, MasterTag } from '../../lib/warehouseLabel';
import { usePermission, useCanSeeWarehouses } from '../../store/auth';
import { fmtQty, isRedundantSku } from '../../lib/format';
import { displayBoxLoose } from '../../lib/packaging';
import { inr2 } from '../../lib/format';

interface Props {
  open: boolean;
  onClose: () => void;
}


/** How many SKUs a product family holds — a STRUCTURAL fact, so it is deliberately not the blue of the
 *  quantity tags in the variant and batch lists below: in this picker blue always means "units in stock". */
const VariantCount = ({ n }: { n: number }) => (
  <Tag tone="purple" style={{ margin: 0, flexShrink: 0 }}>{n} variants</Tag>
);

/**
 * One line of the REQUEST: a product and how much of it. No batch.
 *
 * Raising a transfer asks for goods; it does not reach onto a shelf. Which physical lot goes is the
 * SOURCE warehouse's answer, given when they pick and pack — see the modal doc-comment below. So a
 * line is keyed by product (the same variant added twice merges), and `qty` is total base units with
 * box/loose as display only.
 */
interface Line {
  productId: string;
  sku: string;
  name: string;                 // product family / display name
  variantLabel?: string | null; // e.g. "500ml"
  qty: number;
  unitsPerBox?: number;         // packed line → box/loose display
  unitValue?: number;           // product dealer price (present only when the user may see valuation)
}

// A source-warehouse catalog row, narrowed to what the picker needs.
interface Variant {
  productId: string;
  sku: string;
  name: string;
  variantLabel?: string | null;
  groupCode?: string | null;
  imageUrl?: string | null;
  avail: number;
  boxed: boolean;
  upb: number;
  /** Dealer price — what a batch's `unitValue` used to supply. Same figure, per product. */
  unitValue: number;
}

/**
 * RAISING A STOCK TRANSFER — a REQUEST for goods, not a pick.
 *
 * This screen used to demand a batch: product → variant → BATCH → quantity, with the batch mandatory
 * before Add would light up. That put the choice of physical lot in the hands of whoever raised the
 * transfer, and three things are wrong with that:
 *
 *   • ONLY THE SOURCE CAN SEE THE SHELF. The raiser is often an admin, or the destination's own
 *     manager — not someone standing in the warehouse the goods leave from. Naming a lot they cannot
 *     see is asking them to guess.
 *   • A PRE-PICKED LOT GOES STALE. Between raising and dispatching it can be sold, damaged, blocked
 *     or expire, so the source re-picks anyway and the original choice was noise.
 *   • FEFO BELONGS TO THE PICKER. The dispatch screen takes stock off the earliest-expiring lots.
 *     A requester can pick one that breaks that rule, and the depot then either follows a bad
 *     instruction or silently overrides it.
 *
 * So a line here is product + quantity, and the lot is answered where it is known: the source
 * acknowledges, then picks the batches at Pick & pack (or straight at Dispatch, which demands the
 * same allocations). The server already modelled it this way — `createTransfer` accepts a line with
 * no batch, and calls what this screen produces "placeholder request lines".
 *
 * Availability is still checked, at PRODUCT level against the source warehouse — the same check the
 * server applies — so you cannot ask for more than is there. Which lots cover it is not this
 * screen's question.
 *
 * UNAFFECTED: order-shortfall consolidation transfers. Those are raised by the system, FIFO-resolve
 * their batches and auto-dispatch in one atomic step; no human picks them, so they still carry
 * batches from the moment they are created.
 */
export function TransferFormModal({ open, onClose }: Props) {
  const canSeeWarehouses = useCanSeeWarehouses();
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const canValue = usePermission('STOCK.VALUATION.VIEW');

  const [lines, setLines] = useState<Line[]>([]);
  // Cascade selection: product family → variant (productId) → qty. The batch is NOT chosen here.
  const [familyKey, setFamilyKey] = useState<string | undefined>();
  const [productId, setProductId] = useState<string | undefined>();
  const [qty, setQty] = useState<number>(1);
  /*
   * EMPTY, not zero.
   *
   * These two started at 0, so the boxes opened with a "0" already in them — a figure that means
   * nothing (0 boxes and 0 loose is not a transfer line) and that has to be selected and typed over
   * before a real number can go in. Holding null lets the control render genuinely empty, which is
   * what "nothing entered yet" actually looks like. `entryQty` reads a missing side as zero, so a
   * line of "3 boxes and no loose units" is still just 3 boxes.
   */
  const [box, setBox] = useState<number | null>(null);
  const [loose, setLoose] = useState<number | null>(null);

  // Fetched with the PAGE, not with the modal. This list is the very first thing the form asks for, so
  // gating it on `open` meant the two warehouse dropdowns could be opened before their own call landed —
  // and an empty picker reads as "No results", i.e. the form claiming there are no warehouses at all.
  // The 'warehouses' key is shared by every warehouse picker in the app, so the 5-minute staleTime also
  // makes a re-opened modal instant instead of re-fetching from scratch.
  const { data: warehouses, isFetching: whFetching, isError: whError, refetch: refetchWarehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => request<any[]>({ url: '/warehouses/my' }),
    // Gated so a role without warehouse access doesn't fire a call it is refused — see useCanSeeWarehouses.
    enabled: canSeeWarehouses,
    staleTime: 5 * 60_000,
  });
  // Catalog with live per-warehouse stock — the transfer source list is built from what's actually in the
  // chosen "From" warehouse, so you can't pick (or transfer) stock that isn't there.
  const { data: catalog, isFetching: catalogFetching } = useQuery({
    queryKey: ['catalog-with-stock-transfer'],
    queryFn: () => request<any[]>({ url: '/catalog/with-stock' }),
    enabled: open,
  });

  // Watch both sides so each dropdown can grey out the warehouse already chosen on the other — a transfer's
  // source and destination must differ. The master warehouse is badged in the list.
  const fromId = Form.useWatch('fromWarehouseId', form);
  const toId = Form.useWatch('toWarehouseId', form);
  const whOptions = (disabledId?: string) =>
    (warehouses ?? []).map((w: any) => ({
      value: w.id,
      disabled: disabledId != null && String(w.id) === String(disabledId),
      ...warehouseOption(w),
    }));

  /**
   * What a warehouse dropdown says when it has no options — never the generic "No results", which
   * states as fact that there are no warehouses to transfer between. An empty list here is almost
   * always one of three OTHER things, and each one wants a different move from the reader: the call
   * is still in flight (wait), the call failed (retry — offered right here, so a dropped request
   * doesn't cost the whole form), or the role genuinely has no warehouse (nothing to do).
   */
  const whNotFound = !canSeeWarehouses
    ? 'You do not have access to any warehouse'
    : whFetching
      ? 'Loading warehouses…'
      : whError
        ? (
            <span className="inline-flex flex-wrap items-center justify-center gap-1">
              Could not load warehouses.
              <Button variant="text" size="small" onClick={() => void refetchWarehouses()}>Retry</Button>
            </span>
          )
        : 'No active warehouses';

  // Source availability is warehouse-specific — reset the whole cascade + picked lines whenever "From" changes.
  useEffect(() => {
    setLines([]); setFamilyKey(undefined); setProductId(undefined);
    setQty(1); setBox(null); setLoose(null);
  }, [fromId]);

  const fromCode = (warehouses ?? []).find((w: any) => String(w.id) === String(fromId))?.code as string | undefined;

  // Variants (SKUs) actually in stock in the chosen source warehouse.
  const inStock: Variant[] = (catalog ?? [])
    .map((p: any) => ({
      productId: String(p.productId),
      sku: p.sku,
      name: p.name,
      variantLabel: p.variantLabel ?? null,
      groupCode: p.groupCode ?? null,
      imageUrl: p.imageUrl ?? null,
      avail: Number(p.stock?.warehouses?.[fromCode ?? ''] ?? 0),
      boxed: !!p.boxPackingEnabled && Number(p.unitsPerBox) > 0,
      upb: Number(p.unitsPerBox) || 0,
      unitValue: Number(p.price ?? 0),
    }))
    .filter((p: Variant) => p.avail > 0);

  // Group the in-stock variants into product FAMILIES — siblings share group_code (fall back to display name).
  const families = useMemo(() => {
    const m = new Map<string, { key: string; name: string; image?: string | null; variants: Variant[] }>();
    for (const p of inStock) {
      const key = p.groupCode ? `g:${p.groupCode}` : `n:${p.name}`;
      const e = m.get(key) ?? { key, name: p.name, image: null, variants: [] };
      e.variants.push(p);
      // The family wears the first variant photo there is — siblings of one product are the same
      // bottle in different sizes, so any of them pictures the family.
      e.image = e.image ?? p.imageUrl ?? null;
      m.set(key, e);
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, fromCode]);

  const selFamily = families.find((f) => f.key === familyKey);
  const variants = selFamily?.variants ?? [];
  const selVariant = variants.find((v) => String(v.productId) === String(productId));
  const boxed = !!selVariant?.boxed;
  const upb = selVariant?.upb ?? 0;

  /**
   * The quantity fields, reset for whatever product is about to be selected.
   *
   * A BOX-PACKED variant starts at one box and no loose units. That is not the "0" this form used to
   * open with — 0 boxes and 0 loose is not a quantity at all, it is a figure you have to clear
   * before you can type — whereas one box is the smallest real thing anyone moves, and it mirrors
   * the plain quantity field, which has always defaulted to 1. Loose shows 0 rather than empty
   * because with a box already counted it means something: this many boxes and nothing over.
   *
   * A variant with no box packing has no box field to default, so both stay empty and the plain
   * quantity carries the 1.
   *
   * Takes the product id rather than reading `selVariant`, because every caller is CHANGING the
   * selection and the state it would read is still the previous one.
   */
  const resetEntry = (nextProductId?: string) => {
    const v = variants.find((x) => String(x.productId) === String(nextProductId));
    setQty(1);
    setBox(v?.boxed ? 1 : null);
    setLoose(v?.boxed ? 0 : null);
  };

  const onFamily = (k?: string) => {
    setFamilyKey(k);
    // Skip the friction of a variant step when a family has a single SKU.
    const fam = families.find((f) => f.key === k);
    const only = fam && fam.variants.length === 1 ? fam.variants[0].productId : undefined;
    setProductId(only);
    resetEntry(only);
  };
  const onVariant = (v?: string) => { setProductId(v); resetEntry(v); };

  // Qty already parked on a line for this variant (lines merge per product, so at most one).
  const inLineForVariant = lines.find((l) => String(l.productId) === String(productId))?.qty ?? 0;
  // The ceiling is what the SOURCE WAREHOUSE holds of this product, across all its lots — the same
  // product-level check `createTransfer` applies server-side. Batch-level availability is the
  // source's problem at picking time, by which point it is also current rather than a guess.
  const maxAddable = selVariant ? Math.max(0, Number(selVariant.avail) - inLineForVariant) : 0;
  // The quantity being added: box × upb + loose for a packed variant, else the plain qty.
  const entryQty = boxed ? (box ?? 0) * upb + (loose ?? 0) : qty;

  const addLine = () => {
    if (!fromId) { message.warning('Pick the source warehouse first'); return; }
    if (!selVariant) { message.warning('Pick a product and variant'); return; }
    if (entryQty <= 0) { message.warning('Enter a quantity'); return; }
    if (entryQty > maxAddable + 1e-6) {
      message.warning(`${fromWh?.code ?? 'The source'} holds ${fmtQty(maxAddable)} of ${selVariant.sku}${inLineForVariant ? ` beyond the ${fmtQty(inLineForVariant)} already on this transfer` : ''}`);
      return;
    }
    // Merge into the existing line for the same PRODUCT — asking for the same thing twice is one ask.
    setLines((ls) => {
      const i = ls.findIndex((l) => String(l.productId) === String(selVariant.productId));
      if (i >= 0) { const next = [...ls]; next[i] = { ...next[i], qty: next[i].qty + entryQty }; return next; }
      return [...ls, {
        productId: selVariant.productId, sku: selVariant.sku, name: selVariant.name, variantLabel: selVariant.variantLabel,
        qty: entryQty, unitsPerBox: boxed ? upb : undefined,
        unitValue: selVariant.unitValue || undefined,
      }];
    });
    resetEntry(productId);
  };

  // What the source and destination actually ARE, so the strip below can name them instead of
  // repeating two dropdown values back at the reader.
  const fromWh = (warehouses ?? []).find((w: any) => String(w.id) === String(fromId));
  const toWh = (warehouses ?? []).find((w: any) => String(w.id) === String(toId));
  const sourceUnits = inStock.reduce((t, v) => t + v.avail, 0);

  const totalQty = lines.reduce((s, l) => s + l.qty, 0);
  const totalValue = lines.reduce((s, l) => s + l.qty * (l.unitValue ?? 0), 0);
  const showValue = canValue && lines.length > 0;

  /* The confirm used to say "the stock is committed out of the source warehouse straight away",
     which was never true of a transfer raised this way: it is a REQUEST, and stock leaves only when
     the source dispatches it. Now that the requester does not even name a lot, saying so matters
     more — what they are creating is an ask, and the next move belongs to somebody else. */
  const save = useConfirmedMutation({
    title: 'Send this transfer request?',
    content: 'The source warehouse picks the batches, packs and dispatches it. No stock moves yet.',
    okText: 'Yes, send it',
  }, {
    mutationFn: (vals: any) => request<any>({
      url: '/stock-transfers', method: 'POST',
      data: {
        fromWarehouseId: vals.fromWarehouseId,
        toWarehouseId: vals.toWarehouseId,
        transferDate: vals.transferDate.format('YYYY-MM-DD'),
        remarks: vals.remarks,
        // No batchId: the source picks the lots. The server accepts a batch-less line and the
        // register marks the transfer "lots to pick" until Pick & pack answers it.
        lines: lines.map((l) => ({ productId: l.productId, qty: l.qty })),
      },
    }),
    onSuccess: (r) => {
      // Standalone transfers are created REQUESTED (await dispatch); consolidation transfers come back IN_TRANSIT.
      message.success(`Transfer ${r.transferNo} created · ${r.status === 'IN_TRANSIT' ? 'in transit' : 'awaiting dispatch'}`);
      qc.invalidateQueries({ queryKey: ['stock-transfers'] });
      setLines([]);
      form.resetFields();
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      width={940}
      title="New Stock Transfer"
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() => {
              if (lines.length === 0) { message.warning('Add at least one product'); return; }
              form.submit();
            }}
          >
            Create Transfer
          </Button>
        </>
      }
    >
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        {/* Date · From · To share one row only where each still has the width it needs: a formatted date
            was squeezed below its own text at tablet width, and on a phone three columns left each ~100px.
            Stacked on a phone, From/To side by side on a tablet, the desktop 6/9/9 unchanged. */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8} lg={6}>
            <Form.Item name="transferDate" label="Date" rules={[{ required: true }]} initialValue={dayjs()}>
              <DatePicker className="w-full" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={9}>
            <Form.Item name="fromWarehouseId" label="From" rules={[{ required: true }]}>
              <Select placeholder="Source warehouse" options={whOptions(toId)} loading={whFetching} notFoundContent={whNotFound} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={9}>
            <Form.Item
              name="toWarehouseId"
              label="To"
              dependencies={['fromWarehouseId']}
              rules={[
                { required: true },
                ({ getFieldValue }: any) => ({
                  validator(_: any, v: any) {
                    return v && v === getFieldValue('fromWarehouseId')
                      ? Promise.reject(new Error('Destination must differ from source'))
                      : Promise.resolve();
                  },
                }),
              ]}
            >
              <Select placeholder="Destination warehouse" options={whOptions(fromId)} loading={whFetching} notFoundContent={whNotFound} />
            </Form.Item>
          </Col>
        </Row>

        {/* The journey, once both ends are known. Not a re-print of the two dropdowns: it carries what
            they cannot — which end is the master site, how much is actually pickable at the source, and
            the one consequence of pressing Create. Green is where stock leaves, sky is where it lands,
            the same way the order screen colours a supply route. */}
        {fromWh && toWh && (
          <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border border-line bg-gradient-to-r from-brand-500/8 via-transparent to-sky-500/8 p-3">
            <span className="inline-flex min-w-0 items-center gap-1.5 rounded-control border border-brand-500/25 bg-brand-500/8 px-2.5 py-1.5">
              <Warehouse className="size-3.5 shrink-0 text-brand-700 dark:text-brand-400" />
              <span className="min-w-0 truncate type-body-strong text-content" title={warehouseText(fromWh)}>{fromWh.code}</span>
              {fromWh.isMaster ? <MasterTag /> : null}
            </span>
            <ArrowRight className="size-4 shrink-0 text-subtle" />
            <span className="inline-flex min-w-0 items-center gap-1.5 rounded-control border border-sky-500/25 bg-sky-500/8 px-2.5 py-1.5">
              <Warehouse className="size-3.5 shrink-0 text-sky-700 dark:text-sky-400" />
              <span className="min-w-0 truncate type-body-strong text-content" title={warehouseText(toWh)}>{toWh.code}</span>
              {toWh.isMaster ? <MasterTag /> : null}
            </span>
            <span className="type-caption text-muted">
              {inStock.length
                ? <>{inStock.length} SKU{inStock.length > 1 ? 's' : ''} · {fmtQty(sourceUnits)} units pickable at {fromWh.code}</>
                : <span className="text-warning-600 dark:text-harvest-300">nothing in stock at {fromWh.code} to transfer</span>}
            </span>
            {/* The confirm dialog says this too, but by then the reader has already built the transfer. */}
            <span className="ml-auto inline-flex items-center gap-1.5 type-caption text-subtle">
              <PackageSearch className="size-3.5 text-sky-500" /> {fromWh.code} picks the batches and dispatches — nothing moves yet
            </span>
          </div>
        )}

        {/* Cascading picker: product → variant → batch → qty. Not Form fields (local state), so styled freely. */}
        <div className="mb-3 rounded-card border border-brand-500/25 bg-brand-500/6 p-3">
          <div className="mb-2 inline-flex items-center gap-1.5 type-body-strong text-content">
            <PackagePlus className="size-4 text-brand-600" /> Add stock to transfer
            <span className="type-caption font-normal text-subtle">— product → variant → quantity</span>
          </div>
          <Row gutter={[12, 12]} align="bottom">
            <Col span={12}>
              <div className="mb-1 text-xs text-muted">Product</div>
              <Select
                showSearch
                optionFilterProp="searchLabel"
                // Collapsed trigger draws the same name + count pill as the open list row, so the chosen
                // product reads the same way open or closed.
                optionLabelProp="selectedText"
                className="w-full"
                placeholder={fromId ? 'Select a product' : 'Pick the source warehouse first'}
                disabled={!fromId}
                loading={catalogFetching}
                value={familyKey}
                onChange={onFamily}
                notFoundContent={!fromId ? 'Select a source warehouse' : catalogFetching ? 'Loading products…' : 'No stock in this warehouse'}
                options={families.map((f) => ({
                  value: f.key,
                  // The label is JSX now, so there is no text inside it to match — the family name is.
                  searchLabel: f.name,
                  selectedText: (
                    <span className="inline-flex min-w-0 max-w-full items-center gap-2 align-middle">
                      <EntityAvatar name={f.name} src={f.image} size={20} square />
                      <span className="min-w-0 truncate">{f.name}</span>
                      {f.variants.length > 1 ? <VariantCount n={f.variants.length} /> : null}
                    </span>
                  ),
                  // Identity mark, name, then the variant count as its own pill on the right — the same
                  // row shape as the variant and batch lists below. It used to be one dot-joined string
                  // ("AGN · 2 variants"), which read as part of the product's name instead of a fact about
                  // it; the mark's hue comes from the name, so two adjacent products never look alike.
                  label: (
                    <span className="flex items-center justify-between gap-2">
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        <EntityAvatar name={f.name} src={f.image} size={24} square />
                        <span className="min-w-0 break-words leading-tight">{f.name}</span>
                      </span>
                      {f.variants.length > 1 ? <VariantCount n={f.variants.length} /> : null}
                    </span>
                  ),
                }))}
              />
            </Col>
            <Col span={12}>
              <div className="mb-1 text-xs text-muted">Variant</div>
              <Select
                showSearch
                optionFilterProp="searchLabel"
                className="w-full"
                placeholder={familyKey ? 'Select a variant' : 'Pick a product first'}
                disabled={!familyKey}
                value={productId}
                onChange={onVariant}
                options={variants.map((v) => ({
                  value: v.productId,
                  searchLabel: `${v.variantLabel || v.name} ${v.sku}`,
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ flex: 1, minWidth: 0, lineHeight: 1.25, wordBreak: 'break-word' }}>
                        {v.variantLabel || v.name}{v.sku && !isRedundantSku(v.sku, v.variantLabel || v.name, v.name, `${v.name} ${v.variantLabel ?? ''}`) && <span style={{ color: 'var(--gb-text-muted)' }}> · {v.sku}</span>}
                      </span>
                      <Tag tone="blue" style={{ margin: 0, flexShrink: 0 }}>{fmtQty(v.avail)}</Tag>
                    </span>
                  ),
                }))}
              />
            </Col>
            {/* The batch select gives two columns back to the quantity: it needs room for a batch
                chip and an expiry, not for 360px of it, and the pair of quantity boxes beside it were
                being squeezed into a huddle for width the select was not using. */}
            <Col span={8}>
              <div className="mb-1 text-xs text-muted">{boxed ? 'Quantity (box + loose)' : 'Quantity'}</div>
              {boxed ? (
                /*
                 * TWO CONTROLS, EACH SAYING WHAT IT IS, WITH SPACE BETWEEN THEM.
                 *
                 * They used to sit 4px apart with one loose word, "box", floating in the middle — so
                 * the word read as a label for whichever box the eye landed on, and the second input
                 * had no label at all. Two numbers 4px apart on a form about stock is also just an
                 * invitation to type the loose count into the box count. The unit now lives inside
                 * each control's own frame, and the gap between them is wide enough to read as a
                 * separation rather than a pair of digits.
                 */
                /*
                 * A TWO-COLUMN GRID, not a flex row with fixed widths.
                 *
                 * Sized by hand these two stacked, which is worse than the huddle they replaced: a
                 * fixed width sizes the FIELD and the "box"/"loose" addon hangs off it, so the pair
                 * measured ~286px in a 284px column and wrapped — a two-pixel miss that depends on
                 * the font rendering the addon words. A grid states the intent instead: two cells,
                 * one gap, each control filling its own cell. There is no width left to guess at, so
                 * they cannot wrap, and the gap is the same on any screen.
                 *
                 * `auto-fit` rather than a hard `grid-cols-2`, because two columns is the right answer
                 * only while two columns FIT. Forced, the pair survived down to a 430px phone as two
                 * ~20px slots showing their addons and no room to type a number in — side by side and
                 * useless. The 7rem floor is the width a quantity actually needs; below it the two
                 * drop to one column, which is the honest answer at that size.
                 */
                <div className="grid grid-cols-[repeat(auto-fit,minmax(7rem,1fr))] gap-3">
                  <InputNumber
                    aria-label="Number of boxes"
                    addonAfter="box"
                    value={box}
                    onChange={(v) => setBox(v == null ? null : Math.max(0, Math.floor(v)))}
                    min={0} precision={0} disabled={!productId}
                  />
                  <InputNumber
                    aria-label="Loose units"
                    addonAfter="loose"
                    value={loose}
                    onChange={(v) => setLoose(v == null ? null : Math.max(0, v))}
                    min={0} disabled={!productId}
                  />
                </div>
              ) : (
                <InputNumber placeholder="Qty" value={qty} onChange={(v) => setQty(Number(v ?? 0))} min={0.001}
                  max={selVariant ? maxAddable : undefined} disabled={!productId} className="w-full" />
              )}
            </Col>
            <Col span={4}>
              <Button variant="primary" icon={<Plus size={16} />} onClick={addLine} block
                disabled={!selVariant || entryQty <= 0 || entryQty > maxAddable + 1e-6}>Add</Button>
            </Col>
          </Row>
          {/* Whether what you typed will go in, said in colour as well as in words. This was one grey
              sentence in a hardcoded #8c8c8c that also had to carry the OVER-LIMIT error — the one
              state that must not look like the others (and the hardcode read as light-on-light in
              dark mode). */}
          {selVariant && (() => {
            const over = entryQty > maxAddable + 1e-6;
            const empty = maxAddable <= 0;
            return (
              <div className={cn(
                'mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-control border px-2.5 py-2 type-caption',
                over || empty
                  ? 'border-danger-500/35 bg-danger-500/8 text-danger-600 dark:text-danger-500'
                  : entryQty > 0
                    ? 'border-brand-500/30 bg-brand-500/8 text-brand-700 dark:text-brand-300'
                    : 'border-line bg-surface text-muted',
              )}>
                {over || empty
                  ? <AlertTriangle className="size-3.5 shrink-0" />
                  : entryQty > 0 ? <CheckCircle2 className="size-3.5 shrink-0" /> : <PackageSearch className="size-3.5 shrink-0" />}
                <span>
                  <b>{fmtQty(selVariant.avail)}</b> of <b>{selVariant.sku}</b> at {fromWh?.code ?? 'the source'}
                  {boxed ? ` (${displayBoxLoose(selVariant.avail, upb)} · ${upb}/box)` : ''}
                </span>
                {inLineForVariant > 0 && <Tag tone="neutral">{fmtQty(inLineForVariant)} already added</Tag>}
                {entryQty > 0 && !over && (
                  <Tag tone="green">adding {fmtQty(entryQty)}{boxed ? ` · ${displayBoxLoose(entryQty, upb)}` : ''}</Tag>
                )}
                {canValue && selVariant.unitValue > 0 && entryQty > 0 && !over && (
                  <span className="text-muted">worth {inr2(entryQty * selVariant.unitValue)}</span>
                )}
                {over && <b>you can add at most {fmtQty(maxAddable)}</b>}
              </div>
            );
          })()}
        </div>

        {lines.length === 0 ? (
          <EmptyState
            title="Nothing on this transfer yet"
            description={fromId
              ? 'Pick a product and a quantity above, then press Add. The source warehouse chooses which batches to send when it packs.'
              : 'Choose the source warehouse first — the products you can pick are the ones actually in stock there.'}
          />
        ) : (
          <div className="overflow-hidden rounded-card border border-line">
            {/*
              ONE ROW PER PRODUCT. It used to be one row per BATCH, with the product spanning its
              batches in a merged cell — the right shape when a line named a lot. A request names no
              lot, so a product cannot have several rows any more and the spanning cell had nothing
              left to span.
            */}
            <table className="w-full table-fixed border-collapse">
              <thead>
                <tr className="bg-surface-2 type-overline text-muted">
                  <th className="px-3 py-2 text-left font-medium">Product</th>
                  {/* Wide enough for "45  4 boxes + 5 loose" to stay on one line — the whole point
                      of putting the split beside the figure. */}
                  <th className="w-[156px] px-3 py-2 text-right font-medium">Qty</th>
                  {showValue && <th className="w-[104px] px-3 py-2 text-right font-medium">Value</th>}
                  <th className="w-[52px] px-2 py-2"><span className="sr-only">Remove</span></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => (
                  <tr key={l.productId} className={cn(idx > 0 && 'border-t border-line/70')}>
                    <td className="px-3 py-2.5 align-top">
                      {/* Product and variant are two DIFFERENT facts, so they get two elements: the name reads as
                          the name, and the variant is a chip of its own — the same shape the two pickers above
                          already use. Dot-joined ("AGN · 0.5L") the pack size read as part of the product name. */}
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="type-body-strong leading-tight text-content">{l.name}</span>
                        {l.variantLabel ? <Tag tone="purple" style={{ margin: 0 }}>{l.variantLabel}</Tag> : null}
                      </div>
                      {l.sku && !isRedundantSku(l.sku, l.variantLabel ? `${l.name} ${l.variantLabel}` : l.name, l.name) && (
                        <div className="type-caption text-muted">{l.sku}</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right align-top">
                      {/*
                        The pack split sits BESIDE the figure, not under it — the two are one fact
                        said twice over (45 units IS 4 boxes and 5 loose), so they belong on one
                        line, total first and then how it is packed.
                      */}
                      <span className="inline-flex items-baseline justify-end gap-1.5 whitespace-nowrap">
                        <Tag tone="blue" style={{ margin: 0 }}>{fmtQty(l.qty)}</Tag>
                        {l.unitsPerBox ? <span className="type-caption text-muted">{displayBoxLoose(l.qty, l.unitsPerBox)}</span> : null}
                      </span>
                    </td>
                    {showValue && (
                      <td className="px-3 py-2.5 text-right align-top tabular-nums type-body text-content">
                        {inr2(l.qty * (l.unitValue ?? 0))}
                      </td>
                    )}
                    <td className="px-2 py-2.5 text-right align-top">
                      <Button
                        variant="soft-danger"
                        aria-label={`Remove ${l.variantLabel ? `${l.name} ${l.variantLabel}` : l.name}`}
                        icon={<Trash2 size={16} />}
                        onClick={() => setLines(lines.filter((_, i) => i !== idx))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {lines.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-card border border-brand-500/25 bg-gradient-to-r from-brand-500/10 to-transparent px-3 py-2.5">
            <span className="inline-flex flex-wrap items-center gap-2 type-body text-content">
              <Tag tone="green">{lines.length} product{lines.length > 1 ? 's' : ''}</Tag>
              <b>{fmtQty(totalQty)}</b> units requested from {fromWh?.code ?? 'the source'}
            </span>
            {showValue && (
              <span className="inline-flex items-baseline gap-1.5">
                <span className="type-overline text-brand-700 dark:text-brand-400">Value requested</span>
                <span className="type-metric-sm text-content">{inr2(totalValue)}</span>
              </span>
            )}
          </div>
        )}

        <Form.Item name="remarks" label="Remarks" style={{ marginTop: 12 }}>
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
